import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useCurrency } from '../../../context/CurrencyContext';

const { FiSearch, FiFilter, FiDownload, FiArrowUpRight, FiClock, FiCheckCircle, FiAlertCircle, FiChevronRight } = FiIcons;

const DetailedPayoutHistory = ({ payouts }) => {
  const { formatCurrency } = useCurrency();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const filteredPayouts = payouts.filter(p => {
    const matchesSearch = p.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-50 text-green-600 border-green-100';
      case 'Processing':
        return 'bg-amber-50 text-amber-600 border-amber-100';
      case 'Failed':
        return 'bg-red-50 text-red-600 border-red-100';
      default:
        return 'bg-brand-light text-text-muted border-brand-dark';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed': return FiCheckCircle;
      case 'Processing': return FiClock;
      case 'Failed': return FiAlertCircle;
      default: return FiArrowUpRight;
    }
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-center bg-white p-6 rounded-[32px] border border-brand-dark shadow-sm">
        <div className="relative w-full md:max-w-xs group">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted group-focus-within:text-accent-gold transition-colors" />
          <input 
            type="text" 
            placeholder="Search by Reference ID..." 
            className="w-full pl-11 pr-4 py-3 bg-brand-light border-2 border-transparent rounded-xl focus:border-accent-gold outline-none text-sm transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="flex bg-brand-light p-1 rounded-xl border border-brand-dark overflow-x-auto hide-scrollbar">
            {['All', 'Completed', 'Processing'].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${
                  statusFilter === status 
                  ? 'bg-white text-accent-navy shadow-sm' 
                  : 'text-text-muted hover:text-accent-navy'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
          <button className="p-3 bg-accent-navy text-white rounded-xl hover:bg-accent-gold transition-all shadow-lg shadow-accent-navy/10 flex items-center justify-center shrink-0">
            <SafeIcon icon={FiDownload} />
          </button>
        </div>
      </div>

      {/* Table Header - Desktop Only */}
      <div className="hidden md:grid grid-cols-5 px-8 py-4 text-[10px] font-black uppercase tracking-[0.2em] text-text-muted">
        <div className="col-span-2">Transaction Detail</div>
        <div className="text-center">Date</div>
        <div className="text-center">Status</div>
        <div className="text-right">Amount</div>
      </div>

      {/* List */}
      <div className="space-y-3">
        <AnimatePresence mode="popLayout">
          {filteredPayouts.length > 0 ? (
            filteredPayouts.map((payout, idx) => (
              <motion.div
                key={payout.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white p-6 md:px-8 rounded-[32px] border border-brand-dark hover:border-accent-gold transition-all group shadow-sm hover:shadow-md"
              >
                <div className="grid grid-cols-1 md:grid-cols-5 items-center gap-4">
                  <div className="col-span-2 flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm border ${getStatusStyle(payout.status)}`}>
                      <SafeIcon icon={getStatusIcon(payout.status)} className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-accent-navy">Bank Transfer</p>
                      <p className="text-[10px] text-text-muted font-mono uppercase">Ref: {payout.id}</p>
                    </div>
                  </div>

                  <div className="text-center md:block flex justify-between items-center">
                    <span className="md:hidden text-[9px] font-black uppercase text-text-muted">Date</span>
                    <p className="text-sm text-text-muted font-medium">{payout.date}</p>
                  </div>

                  <div className="flex justify-center md:block text-center">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${getStatusStyle(payout.status)}`}>
                      <div className={`w-1 h-1 rounded-full ${payout.status === 'Completed' ? 'bg-green-500' : 'bg-amber-500'}`} />
                      {payout.status}
                    </span>
                  </div>

                  <div className="text-right md:block flex justify-between items-center border-t border-brand-light md:border-none pt-4 md:pt-0">
                    <span className="md:hidden text-[9px] font-black uppercase text-text-muted">Amount</span>
                    <div className="flex items-center justify-end gap-3">
                      <p className="text-lg font-serif text-accent-navy">{formatCurrency(payout.amount)}</p>
                      <SafeIcon icon={FiChevronRight} className="text-text-muted opacity-0 group-hover:opacity-100 transition-opacity hidden md:block" />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-20 text-center bg-white rounded-[40px] border-2 border-dashed border-brand-dark"
            >
              <SafeIcon icon={FiSearch} className="w-12 h-12 text-text-muted/20 mx-auto mb-4" />
              <p className="text-text-muted font-light italic">No transactions match your search criteria.</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer Info */}
      <div className="p-6 bg-brand-light rounded-3xl border border-brand-dark flex items-center gap-4">
        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-accent-gold shadow-sm">
          <SafeIcon icon={FiAlertCircle} />
        </div>
        <p className="text-[11px] text-text-muted leading-relaxed">
          Payouts are typically processed within 48 hours of request. If a transaction remains in "Processing" for more than 3 business days, please contact <span className="font-bold text-accent-navy underline">merchant-support@onefinallegacy.com</span>.
        </p>
      </div>
    </div>
  );
};

export default DetailedPayoutHistory;